const modals: Record<
  ModalName,
  {
    closable: boolean
    data?: ModalData
    onClose?: () => void
    onSuccess?: () => void
    opened: boolean
    title: string
  }
> = {
  auth: reactive({
    closable: false,
    data: { currentView: 'login' },
    opened: false,
    title: '',
  }),
  list: reactive({
    closable: false,
    opened: false,
    title: '',
  }),
  listEnrichment: reactive({
    closable: true,
    opened: false,
    title: '',
  }),
  spotlight: reactive({
    closable: true,
    opened: false,
    title: '',
  }),
  subscription: reactive({
    closable: true,
    opened: false,
    title: '',
  }),
}

const slideovers: Record<SlideoverName, {
  closable: boolean
  data?: SlideoverData
  onClose?: () => void
  onSuccess?: () => void
  opened: boolean
}> = {
  company: reactive({
    closable: true,
    opened: false,
  }),
  listActions: reactive({
    closable: true,
    opened: false,
  }),
  listSaver: reactive({
    closable: true,
    opened: false,
  }),
  listSignals: reactive({
    closable: true,
    opened: false,
  }),
  suggestions: reactive({
    closable: true,
    opened: false,
  }),
}

export default function useOverlays() {
  const crispIsOpened = useState<boolean>('crisp-is-opened')

  const toggleModal = (params: {
    closable?: boolean
    data?: ModalData
    onClose?: () => void
    onSuccess?: () => void
    opened: boolean
    preserve?: boolean
    title?: string
    type: ModalName
  }) => {
    const target = modals[params.type]

    target.closable = typeof params.closable === 'boolean' ? params.closable : true
    target.data = params.data
    target.opened = params.opened || false
    target.title = params.title || ''

    if (params.preserve) {
      target.onClose = params?.onClose || target?.onClose
      target.onSuccess = params?.onSuccess || target?.onSuccess
    }
    else {
      target.onClose = params.onClose
      target.onSuccess = params.onSuccess
    }
  }

  const toggleSlideover = (params: {
    closable?: boolean
    data?: SlideoverData
    onClose?: () => void
    onSuccess?: () => void
    opened: boolean
    preserve?: boolean
    type: SlideoverName
  }) => {
    const target = slideovers[params.type]

    target.closable = typeof params.closable === 'boolean' ? params.closable : true
    target.data = params.data
    target.opened = params.opened || false

    if (params.preserve) {
      target.onClose = params?.onClose || target?.onClose
      target.onSuccess = params?.onSuccess || target?.onSuccess
    }
    else {
      target.onClose = params.onClose
      target.onSuccess = params.onSuccess
    }
  }

  const isSpotlightOpened = computed(() => {
    return modals.spotlight.opened
  })

  const isModalOpened = computed(() => {
    return Object.entries(modals).find(([_, modal]) => modal.opened)?.[0] as ModalName
  })

  const isSlideoverOpened = computed(() => {
    return Object.entries(slideovers).find(([_, slideover]) => slideover.opened)?.[0] as SlideoverName
  })

  const isOverlayOpened = computed(() => {
    return isModalOpened.value || isSlideoverOpened.value || isSpotlightOpened.value || crispIsOpened.value
  })

  const triggerSuccess = (params: {
    modal: ModalName
    slideover?: SlideoverName
  }) => {
    if (params.slideover) {
      const target = slideovers[params.slideover]

      target.opened = false

      if (target.onSuccess) {
        setTimeout(() => target.onSuccess?.(), 300)
      }
    }

    if (params.modal) {
      const target = modals[params.modal]

      target.opened = false

      if (target.onSuccess) {
        setTimeout(() => target.onSuccess?.(), 300)
      }
    }
  }

  const triggerClose = (params: {
    modal: ModalName
    slideover?: SlideoverName
  }) => {
    if (params.slideover) {
      const target = slideovers[params.slideover]

      target.opened = false

      if (target.onClose) {
        setTimeout(() => target.onClose?.(), 300)
      }
    }

    if (params.modal) {
      const target = modals[params.modal]

      target.opened = false

      if (target.onClose) {
        setTimeout(() => target.onClose?.(), 300)
      }
    }
  }

  return {
    crispIsOpened,
    isModalOpened,
    isOverlayOpened,
    isSlideoverOpened,
    isSpotlightOpened,
    modals,
    slideovers,
    toggleModal,
    toggleSlideover,
    triggerClose,
    triggerSuccess,
  }
}
